import { useCallback, useEffect, useState } from 'react';
import {
  FaChartBar,
  FaImage,
  FaKey,
  FaLock,
  FaPlus,
  FaSave,
  FaSignOutAlt,
  FaSyncAlt,
  FaTrash,
} from 'react-icons/fa';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import NewsLoader from '../NewsLoader/NewsLoader.js';
import { getApiBaseUrl, getPublicAssetUrl } from '../../utils/api.js';
import './AdminPage.css';

const initialLoginForm = {
  login: '',
  password: '',
  remember: false,
};

const initialNewsForm = {
  title: '',
  date: '',
  text: '',
};

const initialGratitudeForm = {
  title: '',
};

const initialGameStats = {
  totalGuests: 0,
  totalRuns: 0,
  averageRunsPerGuest: 0,
  submittedRuns: 0,
  activeOrExpiredRuns: 0,
};

const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const allowedGratitudeFileTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxGratitudeFileSize = 10 * 1024 * 1024;
const newsPerPage = 10;
const adminSessionStorageKey = 'samvdpo-admin-session';
const adminSessionTokenKey = 'samvdpo-admin-token';
const adminSessionDuration = 14 * 24 * 60 * 60 * 1000;
const adminNumberFormatter = new Intl.NumberFormat('ru-RU');

function getAdminImageUrl(imagePath = '') {
  return getPublicAssetUrl(imagePath);
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();

  return { message: text };
}

async function parseCheckedResponse(response, fallbackMessage) {
  const data = await parseResponse(response);

  if (!response.ok) {
    throw new Error(data.message || fallbackMessage);
  }

  return data;
}

function getAdminHeaders(token, hasBody = true) {
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};

  if (token.trim()) {
    headers['x-admin-token'] = token.trim();
  }

  return headers;
}

function clearSavedAdminSession() {
  try {
    localStorage.removeItem(adminSessionStorageKey);
    sessionStorage.removeItem(adminSessionTokenKey);
  } catch (error) {
    // Storage can be unavailable in private or restricted browser modes.
  }
}

function getSavedAdminToken() {
  try {
    const sessionToken = sessionStorage.getItem(adminSessionTokenKey) || '';

    if (sessionToken) {
      return sessionToken;
    }

    const savedSession = JSON.parse(localStorage.getItem(adminSessionStorageKey) || 'null');

    if (!savedSession?.token || !savedSession?.expiresAt) {
      clearSavedAdminSession();
      return '';
    }

    if (Number(savedSession.expiresAt) <= Date.now()) {
      clearSavedAdminSession();
      return '';
    }

    return savedSession.token;
  } catch (error) {
    clearSavedAdminSession();
    return '';
  }
}

function saveAdminSession(token, shouldRemember) {
  try {
    if (shouldRemember) {
      sessionStorage.removeItem(adminSessionTokenKey);
      localStorage.setItem(adminSessionStorageKey, JSON.stringify({
        token,
        expiresAt: Date.now() + adminSessionDuration,
      }));
      return;
    }

    localStorage.removeItem(adminSessionStorageKey);
    sessionStorage.setItem(adminSessionTokenKey, token);
  } catch (error) {
    // If localStorage is blocked, the user will just log in again next time.
  }
}

function NewsFilePreview({ file, index, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const nextPreviewUrl = URL.createObjectURL(file);

    setPreviewUrl(nextPreviewUrl);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [file]);

  return (
    <figure className="adminPage__file-preview">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="adminPage__file-preview-img"
        />
      ) : null}
      <button
        type="button"
        className="adminPage__file-remove-button"
        onClick={() => onRemove(index)}
        aria-label="Удалить изображение"
        title="Удалить изображение"
      >
        <FaTrash />
      </button>
      <figcaption className="adminPage__file-preview-name">
        {file.name}
      </figcaption>
    </figure>
  );
}

function AdminPage() {
  const savedToken = getSavedAdminToken();
  const [adminToken, setAdminToken] = useState(savedToken);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(savedToken));
  const [loginForm, setLoginForm] = useState(initialLoginForm);
  const [news, setNews] = useState([]);
  const [newsPage, setNewsPage] = useState(1);
  const [newsForm, setNewsForm] = useState(initialNewsForm);
  const [newsImageFiles, setNewsImageFiles] = useState([]);
  const [gratitudes, setGratitudes] = useState([]);
  const [gratitudeForm, setGratitudeForm] = useState(initialGratitudeForm);
  const [gratitudeFile, setGratitudeFile] = useState(null);
  const [gameStats, setGameStats] = useState(initialGameStats);
  const [firstCarouselImage, setFirstCarouselImage] = useState('/images/s1.jpg');
  const [carouselImageFile, setCarouselImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [isNewsSubmitting, setIsNewsSubmitting] = useState(false);
  const [isGratitudeSubmitting, setIsGratitudeSubmitting] = useState(false);
  const [isCarouselSubmitting, setIsCarouselSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  function showStatus(type, message) {
    setStatus({ type, message });
  }

  const newsPagesCount = Math.max(1, Math.ceil(news.length / newsPerPage));
  const paginatedNews = news.slice(
    (newsPage - 1) * newsPerPage,
    newsPage * newsPerPage
  );

  const loadAdminData = useCallback(() => {
    setIsLoading(true);

    Promise.all([
      fetch(`${getApiBaseUrl()}/api/news`).then(parseResponse),
      fetch(`${getApiBaseUrl()}/api/settings/carousel`).then(parseResponse),
      fetch(`${getApiBaseUrl()}/api/gratitudes`).then(parseResponse),
      fetch(`${getApiBaseUrl()}/api/admin/game-stats`, {
        headers: getAdminHeaders(adminToken, false),
      }).then((response) => parseCheckedResponse(
        response,
        'Не удалось загрузить статистику игры.',
      )),
    ])
      .then(([newsData, carouselData, gratitudesData, gameStatsData]) => {
        setNews(Array.isArray(newsData) ? newsData : []);
        setGratitudes(Array.isArray(gratitudesData) ? gratitudesData : []);
        setGameStats({
          ...initialGameStats,
          ...gameStatsData,
        });

        if (carouselData.carouselImages?.[0]) {
          setFirstCarouselImage(carouselData.carouselImages[0]);
        }
      })
      .catch((error) => {
        setStatus({
          type: 'error',
          message: error.message || 'Не удалось загрузить данные панели управления.',
        });
      })
      .finally(() => setIsLoading(false));
  }, [adminToken]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAdminData();
    }
  }, [isAuthenticated, loadAdminData]);

  useEffect(() => {
    setNewsPage((currentPage) => Math.min(currentPage, newsPagesCount));
  }, [newsPagesCount]);

  function handleLoginChange(event) {
    const {
      checked, name, type, value,
    } = event.target;

    setLoginForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  async function handleLoginSubmit(event) {
    event.preventDefault();
    setIsLoginSubmitting(true);
    showStatus('', '');

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login: loginForm.login.trim(),
          password: loginForm.password,
        }),
      });
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось войти в панель управления.');
      }

      setAdminToken(data.token);
      saveAdminSession(data.token, loginForm.remember);
      setLoginForm(initialLoginForm);
      setIsAuthenticated(true);
      showStatus('', '');
    } catch (error) {
      showStatus('error', error.message || 'Не удалось войти в панель управления.');
    } finally {
      setIsLoginSubmitting(false);
    }
  }

  function handleLogout() {
    clearSavedAdminSession();
    setAdminToken('');
    setIsAuthenticated(false);
    setNews([]);
    setGratitudes([]);
    setGameStats(initialGameStats);
    showStatus('', '');
  }

  function handleNewsChange(event) {
    const { name, value } = event.target;

    setNewsForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function buildNewsPayload() {
    return {
      title: newsForm.title.trim(),
      date: newsForm.date.trim(),
      text: newsForm.text
        .split('\n')
        .map((paragraph) => paragraph.trim())
        .filter(Boolean),
      images: [],
      link: '',
    };
  }

  function buildNewsFormData() {
    const payload = buildNewsPayload();
    const formData = new FormData();

    formData.append('title', payload.title);
    formData.append('date', payload.date);
    formData.append('text', JSON.stringify(payload.text));
    formData.append('images', JSON.stringify(payload.images));
    formData.append('link', payload.link);
    newsImageFiles.forEach((file) => formData.append('newsImages', file));

    return formData;
  }

  function handleNewsFilesChange(event) {
    const selectedFiles = Array.from(event.target.files || []);
    const imageFiles = selectedFiles.filter((file) => (
      allowedImageTypes.includes(file.type)
    ));

    setNewsImageFiles((current) => [...current, ...imageFiles]);
    event.target.value = '';

    if (selectedFiles.length !== imageFiles.length) {
      showStatus('error', 'Можно загрузить только JPG, PNG, WEBP или GIF.');
    }
  }

  function handleRemoveNewsFile(fileIndex) {
    setNewsImageFiles((current) => current.filter((_, index) => index !== fileIndex));
  }

  function handleCarouselFileChange(event) {
    const [selectedFile] = Array.from(event.target.files || []);

    if (!selectedFile) {
      setCarouselImageFile(null);
      return;
    }

    if (!allowedImageTypes.includes(selectedFile.type)) {
      setCarouselImageFile(null);
      showStatus('error', 'Можно загрузить только JPG, PNG, WEBP или GIF.');
      return;
    }

    setCarouselImageFile(selectedFile);
    showStatus('', '');
  }

  function handleGratitudeFileChange(event) {
    const [selectedFile] = Array.from(event.target.files || []);

    if (!selectedFile) {
      setGratitudeFile(null);
      return;
    }

    if (!allowedGratitudeFileTypes.includes(selectedFile.type)) {
      setGratitudeFile(null);
      showStatus('error', 'Благодарность должна быть в формате JPG, PNG или WEBP.');
      return;
    }

    if (selectedFile.size > maxGratitudeFileSize) {
      setGratitudeFile(null);
      showStatus('error', 'Размер изображения не должен превышать 10 МБ.');
      return;
    }

    setGratitudeFile(selectedFile);
    showStatus('', '');
  }

  async function handleNewsSubmit(event) {
    event.preventDefault();
    setIsNewsSubmitting(true);
    showStatus('', '');

    try {
      const hasUploadedFiles = newsImageFiles.length > 0;
      const response = await fetch(`${getApiBaseUrl()}/api/news`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken, !hasUploadedFiles),
        body: hasUploadedFiles
          ? buildNewsFormData()
          : JSON.stringify(buildNewsPayload()),
      });
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось добавить новость.');
      }

      setNewsForm(initialNewsForm);
      setNewsImageFiles([]);
      showStatus('success', 'Новость добавлена.');
      loadAdminData();
    } catch (error) {
      showStatus('error', error.message || 'Не удалось добавить новость.');
    } finally {
      setIsNewsSubmitting(false);
    }
  }

  async function handleDeleteNews(newsItem) {
    const isConfirmed = window.confirm(`Удалить новость "${newsItem.title}"?`);

    if (!isConfirmed) {
      return;
    }

    showStatus('', '');

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/news/${newsItem._id}`,
        {
          method: 'DELETE',
          headers: getAdminHeaders(adminToken, false),
        }
      );
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось удалить новость.');
      }

      setNews((current) => current.filter((item) => item._id !== newsItem._id));
      showStatus('success', 'Новость удалена.');
    } catch (error) {
      showStatus('error', error.message || 'Не удалось удалить новость.');
    }
  }

  async function handleGratitudeSubmit(event) {
    event.preventDefault();

    if (!gratitudeFile) {
      showStatus('error', 'Выберите файл благодарности.');
      return;
    }

    setIsGratitudeSubmitting(true);
    showStatus('', '');

    try {
      const formData = new FormData();

      formData.append('title', gratitudeForm.title.trim());
      formData.append('gratitudeFile', gratitudeFile);

      const response = await fetch(`${getApiBaseUrl()}/api/admin/gratitudes`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken, false),
        body: formData,
      });
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось добавить благодарность.');
      }

      setGratitudeForm(initialGratitudeForm);
      setGratitudeFile(null);
      setGratitudes((current) => [data, ...current]);
      showStatus('success', 'Благодарность добавлена и стала первой в карусели.');
    } catch (error) {
      showStatus('error', error.message || 'Не удалось добавить благодарность.');
    } finally {
      setIsGratitudeSubmitting(false);
    }
  }

  async function handleDeleteGratitude(gratitudeItem) {
    const isConfirmed = window.confirm(`Удалить благодарность "${gratitudeItem.title}"?`);

    if (!isConfirmed) {
      return;
    }

    showStatus('', '');

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/gratitudes/${gratitudeItem._id}`,
        {
          method: 'DELETE',
          headers: getAdminHeaders(adminToken, false),
        }
      );
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось удалить благодарность.');
      }

      setGratitudes((current) => current.filter((item) => item._id !== gratitudeItem._id));
      showStatus('success', 'Благодарность удалена.');
    } catch (error) {
      showStatus('error', error.message || 'Не удалось удалить благодарность.');
    }
  }

  async function handleCarouselSubmit(event) {
    event.preventDefault();
    setIsCarouselSubmitting(true);
    showStatus('', '');

    try {
      const hasUploadedFile = Boolean(carouselImageFile);
      const formData = new FormData();

      if (hasUploadedFile) {
        formData.append('carouselImage', carouselImageFile);
      }

      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/settings/carousel`,
        {
          method: 'PUT',
          headers: getAdminHeaders(adminToken, !hasUploadedFile),
          body: hasUploadedFile
            ? formData
            : JSON.stringify({
              firstImage: firstCarouselImage.trim(),
            }),
        }
      );
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(data.message || 'Не удалось обновить карусель.');
      }

      setFirstCarouselImage(data.carouselImages?.[0] || firstCarouselImage);
      setCarouselImageFile(null);
      showStatus('success', 'Первое изображение карусели обновлено.');
    } catch (error) {
      showStatus('error', error.message || 'Не удалось обновить карусель.');
    } finally {
      setIsCarouselSubmitting(false);
    }
  }

  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <main className="content">
          <section className="adminPage adminPage_login">
            <form className="adminPage__login-panel" onSubmit={handleLoginSubmit}>
              <div className="adminPage__panel-heading">
                <FaLock className="adminPage__panel-icon" />
                <div>
                  <p className="adminPage__kicker">Закрытый раздел</p>
                  <h2 className="adminPage__title adminPage__title_login">
                    Вход в панель управления
                  </h2>
                </div>
              </div>

              {status.message ? (
                <p className={`adminPage__status adminPage__status_${status.type}`}>
                  {status.message}
                </p>
              ) : null}

              <label className="adminPage__field">
                <span className="adminPage__label">Логин</span>
                <input
                  type="text"
                  name="login"
                  className="adminPage__input"
                  value={loginForm.login}
                  onChange={handleLoginChange}
                  autoComplete="username"
                  required
                />
              </label>

              <label className="adminPage__field">
                <span className="adminPage__label">Пароль</span>
                <input
                  type="password"
                  name="password"
                  className="adminPage__input"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  autoComplete="current-password"
                  required
                />
              </label>

              <label className="adminPage__checkbox-field">
                <input
                  type="checkbox"
                  name="remember"
                  className="adminPage__checkbox"
                  checked={loginForm.remember}
                  onChange={handleLoginChange}
                />
                <span>Запомнить меня</span>
              </label>

              <button
                type="submit"
                className="adminPage__button adminPage__button_full"
                disabled={isLoginSubmitting}
              >
                <FaKey />
                {isLoginSubmitting ? 'Проверка...' : 'Войти'}
              </button>
            </form>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="content">
        <section className="adminPage">
          <div className="adminPage__hero adminPage__hero_auth">
            <div>
              <p className="adminPage__kicker">Управление сайтом</p>
              <h2 className="adminPage__title">Панель управления сайтом</h2>
              <p className="adminPage__lead">
                Добавление и удаление новостей, а также замена первого
                изображения на главной карусели.
              </p>
            </div>
            <button
              type="button"
              className="adminPage__button adminPage__button_secondary"
              onClick={handleLogout}
            >
              <FaSignOutAlt />
              Выйти
            </button>
          </div>

          {status.message ? (
            <p className={`adminPage__status adminPage__status_${status.type}`}>
              {status.message}
            </p>
          ) : null}

          <section className="adminPage__panel adminPage__stats-panel">
            <div className="adminPage__panel-heading">
              <FaChartBar className="adminPage__panel-icon" />
              <div>
                <h3 className="adminPage__panel-title">Статистика игры</h3>
                <p className="adminPage__panel-text">
                  Учитываются все запущенные забеги с момента включения игровых сессий.
                </p>
              </div>
            </div>
            <div className="adminPage__stats-grid">
              <article className="adminPage__stat-item">
                <span className="adminPage__stat-label">Гостей сыграло</span>
                <strong className="adminPage__stat-value">
                  {adminNumberFormatter.format(gameStats.totalGuests)}
                </strong>
              </article>
              <article className="adminPage__stat-item">
                <span className="adminPage__stat-label">Среднее ранов на гостя</span>
                <strong className="adminPage__stat-value">
                  {Number(gameStats.averageRunsPerGuest || 0).toLocaleString('ru-RU', {
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </article>
              <article className="adminPage__stat-item adminPage__stat-item_compact">
                <span className="adminPage__stat-label">Всего ранов</span>
                <strong className="adminPage__stat-value">
                  {adminNumberFormatter.format(gameStats.totalRuns)}
                </strong>
              </article>
              <article className="adminPage__stat-item adminPage__stat-item_compact">
                <span className="adminPage__stat-label">Сохранено результатов</span>
                <strong className="adminPage__stat-value">
                  {adminNumberFormatter.format(gameStats.submittedRuns)}
                </strong>
              </article>
            </div>
          </section>

          <section className="adminPage__grid">
            <form className="adminPage__panel" onSubmit={handleNewsSubmit}>
              <div className="adminPage__panel-heading">
                <FaPlus className="adminPage__panel-icon" />
                <div>
                  <h3 className="adminPage__panel-title">Добавить новость</h3>
                  
                </div>
              </div>

              <label className="adminPage__field">
                <span className="adminPage__label">Заголовок</span>
                <input
                  type="text"
                  name="title"
                  className="adminPage__input"
                  value={newsForm.title}
                  onChange={handleNewsChange}
                  minLength="2"
                  maxLength="200"
                  required
                />
              </label>

              <label className="adminPage__field">
                <span className="adminPage__label">Дата</span>
                <input
                  type="text"
                  name="date"
                  className="adminPage__input"
                  value={newsForm.date}
                  onChange={handleNewsChange}
                  placeholder="31 мая 2026"
                  required
                />
              </label>

              <label className="adminPage__field">
                <span className="adminPage__label">Текст новости</span>
                <textarea
                  name="text"
                  className="adminPage__input adminPage__textarea"
                  value={newsForm.text}
                  onChange={handleNewsChange}
                  required
                />
              </label>


              <label className="adminPage__field">
                <span className="adminPage__label">Загрузить новые изображения</span>
                <span className="adminPage__hint">JPG, PNG, WEBP или GIF. Можно выбрать несколько файлов. Обложкой станет первое выбранное изображение.</span>
                <input
                  type="file"
                  className="adminPage__input adminPage__file-input"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handleNewsFilesChange}
                />
              </label>

              {newsImageFiles.length > 0 ? (
                <div className="adminPage__file-preview-list">
                  {newsImageFiles.map((file, index) => (
                    <NewsFilePreview
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                      file={file}
                      index={index}
                      onRemove={handleRemoveNewsFile}
                    />
                  ))}
                </div>
              ) : null}


              <button
                type="submit"
                className="adminPage__button"
                disabled={isNewsSubmitting}
              >
                <FaPlus />
                {isNewsSubmitting ? 'Добавление...' : 'Добавить новость'}
              </button>
            </form>

            <form className="adminPage__panel" onSubmit={handleCarouselSubmit}>
              <div className="adminPage__panel-heading">
                <FaImage className="adminPage__panel-icon" />
                <div>
                  <h3 className="adminPage__panel-title">Карусель</h3>
                  <p className="adminPage__panel-text">
                    Меняется только первое изображение на главной странице.
                  </p>
                </div>
              </div>

              <label className="adminPage__field">
                <span className="adminPage__label">Загрузить первое изображение</span>
                <span className="adminPage__hint">
                  JPG, PNG, WEBP или GIF. После сохранения файл станет первым слайдом на главной странице.
                </span>
                <input
                  type="file"
                  className="adminPage__input adminPage__file-input"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleCarouselFileChange}
                />
              </label>

              <img
                src={carouselImageFile
                  ? URL.createObjectURL(carouselImageFile)
                  : getAdminImageUrl(firstCarouselImage)}
                alt=""
                className="adminPage__preview"
              />
              {carouselImageFile ? (
                <p className="adminPage__selected-file">
                  Выбрано: {carouselImageFile.name}
                </p>
              ) : null}

              <button
                type="submit"
                className="adminPage__button"
                disabled={isCarouselSubmitting}
              >
                <FaSave />
                {isCarouselSubmitting ? 'Сохранение...' : 'Сохранить картинку'}
              </button>
            </form>
          </section>

          <section className="adminPage__panel">
            <div className="adminPage__panel-heading">
              <FaImage className="adminPage__panel-icon" />
              <div>
                <h3 className="adminPage__panel-title">Благодарности и награды</h3>
                <p className="adminPage__panel-text">
                  Новая благодарность станет первой в карусели раздела «О нас».
                </p>
              </div>
            </div>

            <form className="adminPage__gratitude-form" onSubmit={handleGratitudeSubmit}>
              <label className="adminPage__field">
                <span className="adminPage__label">Название</span>
                <input
                  type="text"
                  className="adminPage__input"
                  value={gratitudeForm.title}
                  onChange={(event) => setGratitudeForm({ title: event.target.value })}
                  minLength="2"
                  maxLength="200"
                  placeholder="Например: Благодарственное письмо школы № 29"
                  required
                />
              </label>

              <label className="adminPage__field">
                <span className="adminPage__label">Изображение благодарности</span>
                <span className="adminPage__hint">
                  JPG, PNG или WEBP, не более 10 МБ. Рекомендуем вертикальное изображение
                  хорошего качества с соотношением сторон около 3:4.
                </span>
                <input
                  type="file"
                  className="adminPage__input adminPage__file-input"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleGratitudeFileChange}
                  required
                />
              </label>

              <div className="adminPage__gratitude-previews">
                {gratitudeFile ? (
                  <img
                    src={URL.createObjectURL(gratitudeFile)}
                    alt=""
                    className="adminPage__gratitude-preview"
                  />
                ) : null}
                <div>
                  {gratitudeFile ? (
                    <p className="adminPage__selected-file">Выбрано: {gratitudeFile.name}</p>
                  ) : null}
                </div>
              </div>

              <button
                type="submit"
                className="adminPage__button"
                disabled={isGratitudeSubmitting}
              >
                <FaPlus />
                {isGratitudeSubmitting ? 'Добавление...' : 'Добавить благодарность'}
              </button>
            </form>

            {gratitudes.length > 0 ? (
              <div className="adminPage__gratitude-list">
                {gratitudes.map((item) => (
                  <article className="adminPage__gratitude-item" key={item._id}>
                    <img
                      src={getAdminImageUrl(item.preview)}
                      alt=""
                      className="adminPage__gratitude-list-image"
                    />
                    <h4 className="adminPage__news-title">{item.title}</h4>
                    <button
                      type="button"
                      className="adminPage__icon-button"
                      onClick={() => handleDeleteGratitude(item)}
                      aria-label={`Удалить благодарность ${item.title}`}
                    >
                      <FaTrash />
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <p className="adminPage__panel-text">
                Через панель управления пока не добавлено ни одной благодарности.
              </p>
            )}
          </section>

          <section className="adminPage__panel">
            <div className="adminPage__panel-heading adminPage__panel-heading_row">
              <div className="adminPage__panel-heading">
                <FaTrash className="adminPage__panel-icon" />
                <div>
                  <h3 className="adminPage__panel-title">Новости</h3>
                  <p className="adminPage__panel-text">
                    Список загружается в том же порядке, что и на сайте.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="adminPage__button adminPage__button_secondary"
                onClick={loadAdminData}
              >
                <FaSyncAlt />
                Обновить
              </button>
            </div>

            {isLoading ? (
              <div className="adminPage__loader">
                <NewsLoader text="Загрузка панели управления..." />
              </div>
            ) : (
              <>
              <div className="adminPage__news-list">
                {paginatedNews.map((item) => (
                  <article className="adminPage__news-item" key={item._id}>
                    <div>
                      <p className="adminPage__news-date">{item.date}</p>
                      <h4 className="adminPage__news-title">{item.title}</h4>
                    </div>
                    <button
                      type="button"
                      className="adminPage__icon-button"
                      onClick={() => handleDeleteNews(item)}
                      aria-label={`Удалить новость ${item.title}`}
                    >
                      <FaTrash />
                    </button>
                  </article>
                ))}
              </div>
              {newsPagesCount > 1 ? (
                <div className="adminPage__pagination">
                  <button
                    type="button"
                    className="adminPage__pagination-button"
                    onClick={() => setNewsPage((currentPage) => currentPage - 1)}
                    disabled={newsPage === 1}
                  >
                    Назад
                  </button>
                  <span className="adminPage__pagination-status">
                    {newsPage} / {newsPagesCount}
                  </span>
                  <button
                    type="button"
                    className="adminPage__pagination-button"
                    onClick={() => setNewsPage((currentPage) => currentPage + 1)}
                    disabled={newsPage === newsPagesCount}
                  >
                    Вперед
                  </button>
                </div>
              ) : null}
              </>
            )}
          </section>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default AdminPage;
